import { Component, ViewChild, OnInit, Input, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, map, finalize, debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiService, TouchPointFilterParams } from '@app/services/api.service';
import { MsalService } from '@azure/msal-angular';
import { PreviousRouteService } from '@app/services/previous-route.service';
import { AppConfigService } from '@eucoms/common-components';
import { AccountTouchPointSummary } from '@eucoms/customer-touchpoints-tracker';
import { SessionSummary } from '@eucoms/customer-touchpoints-tracker';

@Component({
  selector: 'app-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['../../app.component.scss', './preview.component.scss']
})
export class RealTimePreviewComponent implements OnInit, OnDestroy {
  // Whether this is a production build or not
  isProduction: boolean = environment.production;

  // The OpCo Selected in the base app component
  @Input() opCoSelected: string;

  // Whether to show the Search Account Number section
  showSearchAccount: boolean = true;

  // The data set of summaries
  fetchedSummary: AccountTouchPointSummary | null = null;

  // Account number values
  searchAccountNumber: string = '';
  accountNumberFromRoute: string = '';

  // Loading indicator
  isLoading: boolean = false;

  // Admin roles and feedback disable flag
  adminRoles: string[] = environment.appConfig.adminRoles || [];
  feedbackDisabled: boolean = false; // computed on init
  
  feedbackState: { [key: string]: 'up' | 'down' | null } = {}; // Centralized feedback state (key = sessionId_resultCode)

  private feedbackSubject = new Subject<{sessionId: string; resultCode: string | null; state: 'up' | 'down' | null}>();
  // Route subscriptions and Urls
  private routeSubscription: Subscription;
  private previousUrlSubscription: Subscription | undefined;
  private previousUrl: string | null = null;
  private currentUrl: string | null = null;

  constructor(
    private appConfig: AppConfigService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private previousRouteService: PreviousRouteService,
    private authService: MsalService
  ) {
  }

  ngOnInit(): void {
    this.opCoSelected ??= this.appConfig.config.opCo;

    this.feedbackSubject.pipe(debounceTime(500)).subscribe(event => this.submitFeedback(event));

    this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.accountNumberFromRoute = params.get('accountNumber');
      if (this.accountNumberFromRoute) {
        this.showSearchAccount = false;
        this.searchAccountNumber = this.accountNumberFromRoute;
        this.searchSummaries();
      } else {
        this.showSearchAccount = true;
      }
    });

    this.previousUrlSubscription = this.previousRouteService.getPreviousUrl().subscribe((url) => {
      this.previousUrl = url;
    });

    this.currentUrl = this.router.url;

    // Compute feedback disabled state
    this.feedbackDisabled = this.isFeedbackDisabled();
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.previousUrlSubscription) {
      this.previousUrlSubscription.unsubscribe();
    }
  }

  // 'disabled' means production and NOT admin.
  private isFeedbackDisabled(): boolean {
    if (!environment.production) return false; // enabled in non-prod
    return !this.isAdmin();
  }

  private isAdmin(): boolean {
    return this.adminRoles.some(r => this.hasRole(r));
  }

  private hasRole(role: string): boolean {
    const idTokenClaims: any = this.authService.instance.getActiveAccount()?.idTokenClaims;
    if (!idTokenClaims) return false;
    const userRoles: string[] = (idTokenClaims['roles'] as string[]) || [];
    return userRoles.includes(role);
  }
  
  get csrId(): string {
    const acct = this.authService.instance.getActiveAccount();
    if (!acct?.username) return '';
    return 'ssip_' + acct.username.split('@')[0];
  }

  onBackClick(): void {
    if (this.previousUrl) {
      if (this.previousUrl.toLowerCase() === this.currentUrl?.toLowerCase()) {
        // If on preview details after Account Number search,
        // Reload page with empty Account Number Search
        this.showSearchAccount = true;
        // Reset search
        this.searchAccountNumber = '';
        // Clear data set
        this.fetchedSummary = null;
      } else {
        // Otherwise, navigate to previous SSIP page
        this.router.navigate([this.previousUrl]);
      }
    } else {
      // If no previousURL, accessed page from outside SSIP
      if (window.history.state == null) {
        if (this.accountNumberFromRoute) {
          // Reload page with empty Account Number Search
          this.showSearchAccount = true;
          // Reset search
          this.searchAccountNumber = '';
          // Clear data set
          this.fetchedSummary = null;
        }
      } else {
        // Navigate back to previous page outside SSIP
        this.location.back();
      }
    }
  }

  doSearch(): void {
    // Set previousUrl to current page, for Back link usage
    this.previousUrl = this.currentUrl;
    this.searchSummaries();
  }

  searchSummaries(): void {
    this.isLoading = true;

    this.apiService.getSummaryFromProxy(this.searchAccountNumber)
      .pipe(
        map((response: any) => {
          if (response) {
            return response;
          } else {
            console.warn('No data received from API filter, showing empty list.');
            return null;
          }
        }),
        finalize(() => {
          this.isLoading = false;
          this.showSearchAccount = false;
          console.log('Search summaries data complete.');
        }),
        catchError((error) => {
          console.error('Error searching summaries data: ', error);
          this.isLoading = false;
          this.showSearchAccount = false;
          return of(null);
        })
      )
      .subscribe((data: AccountTouchPointSummary | null) => {
        this.fetchedSummary = data;
        console.log('this.fetchedSummary = ', this.fetchedSummary);
        this.preloadFeedbackState();
      });
  }

  clearSearch(): void {
    // Reset search
    this.searchAccountNumber = '';
    // Clear data set
    this.fetchedSummary = null;
    this.feedbackState = {};
  }

  isPHI(): boolean {
    const opcoString = this.opCoSelected?.toString().toLowerCase(); // Ensure lowercase comparison
    return ['ace', 'dpl', 'pepco'].includes(opcoString);
  }

  // ===================== Feedback Logic (centralized) =====================
  private getFeedbackKey(sessionId: string, resultCode: string): string {
    return `${sessionId}_${resultCode ?? ''}`;
  }

  getFeedbackStateFor = (session: SessionSummary): 'up' | 'down' | null => {
    return this.feedbackState[this.getFeedbackKey(session.sessionId, session.resultCode)] || null;
  };

  onFeedbackChange(event: { sessionId: string; resultCode: string | null; state: 'up' | 'down' | null }): void {
    const resultCode = event.resultCode ?? '';
    const key = this.getFeedbackKey(event.sessionId, resultCode);
    this.feedbackState[key] = event.state;

    this.feedbackSubject.next(event);
  }

  private submitFeedback(event: { sessionId: string; resultCode: string | null; state: 'up' | 'down' | null }) {
    const resultCode = event.resultCode ?? '';
    const action = event.state === 'up' ? 'CastUpVote' : event.state === 'down' ? 'CastDownVote' : 'CastResetVote';
    
    this.apiService.submitSummaryFeedback(this.fetchedSummary.accountNumber, event.sessionId, action, resultCode, this.csrId)
      .subscribe({
        next: (resp) => console.log('Feedback submitted successfully', resp),
        error: (err) => console.error('Error submitting feedback', err)
      });
  }

  private preloadFeedbackState(): void {
    if (!this.fetchedSummary?.sessionSummaries?.length || !this.csrId) return;
    this.fetchedSummary.sessionSummaries.forEach(session => {
      const matching = session.feedbackResponses?.find(r => r.csrId === this.csrId && (r.resultCode ?? '') === (session.resultCode ?? ''));
      if (matching) {
        const key = this.getFeedbackKey(session.sessionId, session.resultCode);
        this.feedbackState[key] = this.mapServerResponseToState(matching.status);
      } 
    });
  }

  private mapServerResponseToState(response: string): 'up' | 'down' | null {
    switch ((response ?? '').toLowerCase()) {
      case 'upvote': return 'up';
      case 'downvote': return 'down';
      default: return null;
    }
  }

}


